import { Layout, Text } from '@oneaudi/unified-web-alpha-components';
import { useCallback, useMemo } from 'react';
import MonthlyRateFootnote from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';
import PriceFootnote from '~/app/compositions/content/calculation-info/components/price-footnote';
import { PriceSectionStyle } from '~/app/compositions/content/components/styles/section-style';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { StyledTextLinkButton } from '~/components/dialog/styles/styled-dialog';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { Accessories } from '~/app/compositions/content/configuration-overview-builder';
import { getNonFinanceableFilteredPriceParts } from '~/app/utils/FilteredPriceParts';

export function PriceSection({
	totalPrice,
	monthlyRate,
	remark,
	showSpecialOffers,
	disclaimer,
}: {
	totalPrice: string;
	monthlyRate: string | null | undefined;
	remark: string | null | undefined;
	showSpecialOffers: boolean;
	disclaimer?: string;
}) {
	const { i18n } = useI18n();
	const { carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { showIframeDialog } = useIframeDialog();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const { priceSummary } = useCatalog();
	const nonFinanceablePriceParts = getNonFinanceableFilteredPriceParts(priceSummary, config);
	const nonFinanceablePricePartDisclaimer = i18n('ngc.remarks.nonFinanceablePricePartDisclaimer');

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	const onButtonClick = useCallback(
		(url: string, eventName: LayerType, label: string) => {
			config.useIframe ? showIframeDialog({ url, name: eventName }) : window.open(url);
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator (configuration overview) - click to open ${eventName}`,
				componentName: 'configurator-configuration-overview',
				label,
				targetURL: url,
			});
			appTrackingService.updateLayerComponent(eventName, 'open');

			config.useIframe &&
				appTrackingService.viewChangeEvent({
					eventName: `configurator - ${eventName}`,
					componentName: eventName.split(' ').join('-'),
				});
		},
		[config.useIframe, showIframeDialog],
	);

	return (
		<PriceSectionStyle>
			{}
			<div tabIndex={0} className="price-grid">
				<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{i18n('ngc.priceDetails.totalPrice')}
					{config.enablePriceDisclaimer && <PriceFootnote text={priceDisclaimerText} />}
				</Text>

				<Text className="alignRight" tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{totalPrice}
				</Text>
			</div>
			{monthlyRate && !config.hideMonthlyRates && (
				<div tabIndex={0} className="price-grid">
					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
						{i18n('ngc.priceDetails.privateLeasing')}
					</Text>

					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }} className="alignRight">
						{monthlyRate}
						<MonthlyRateFootnote text={disclaimer} />
					</Text>
				</div>
			)}
			<Layout
				direction={{
					default: 'column',
					md: 'row',
				}}
				columnGap="24px"
				rowGap="8px">
				{config.deliveryCostUrl && (
					<StyledTextLinkButton
						size="sm"
						variant="standalone"
						onClick={() =>
							onButtonClick(
								config.deliveryCostUrl,
								LayerType.DeliveryCostLayer,
								i18n('ngc.priceDetails.deliveryCosts'),
							)
						}>
						{i18n('ngc.priceDetails.deliveryCosts')}
					</StyledTextLinkButton>
				)}
				{showSpecialOffers && (
					<StyledTextLinkButton
						size="sm"
						variant="standalone"
						onClick={() =>
							onButtonClick(
								config.specialOfferUrl,
								LayerType.SpecialOffersLayer,
								i18n('ngc.actions.viewSpecialOffers'),
							)
						}>
						{i18n('ngc.actions.viewSpecialOffers')}
					</StyledTextLinkButton>
				)}
			</Layout>

			{config.uiPreferences.prices.nonFinanceablePriceParts?.includes('accessories') &&
				nonFinanceablePriceParts &&
				nonFinanceablePriceParts?.length > 0 && (
					<>
						<div className="mt-4">
							<Accessories />
						</div>
						{nonFinanceablePricePartDisclaimer && (
							<div tabIndex={0}>
								<Text
									className="mt-4"
									tag={'p'}
									variant={{ type: 'copy', order: '2', style: 'normal' }}>
									{i18n('ngc.remarks.nonFinanceablePricePartDisclaimer')}
								</Text>
							</div>
						)}
					</>
				)}

			{remark && (
				<div tabIndex={0}>
					<Text className="mt-4" tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
						<span dangerouslySetInnerHTML={{ __html: remark }} />
					</Text>
				</div>
			)}
		</PriceSectionStyle>
	);
}
